import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import { useMsal } from '@azure/msal-react';
import KarmaApiService from '../services/karmaApi';
import smsApiService from '../services/smsApiService';
import { isSmsAdminAccount } from '../utils/smsPermissions';
import messageConfigService from '../services/smsMessageConfigService';
import bookingOptionsService from '../services/bookingOptionsService';

const APPOINTMENT_TYPES = {
  WHA: {
    id: 'WHA',
    label: 'Whole House Assessment (WHA)',
    body: 'to arrange the booking of a Whole House Assessment (WHA) for',
    duration: 'The assessment will take around 1-2 hours'
  },
  EPC: {
    id: 'EPC',
    label: 'Energy Performance Certificate (EPC)',
    body: 'to arrange the booking of an Energy Performance Certificate (EPC) for',
    duration: 'The assessment will take around 1 hour'
  },
  POST_EPC: {
    id: 'POST_EPC',
    label: 'Post installation Energy Performance certificate (EPC)',
    body: 'following the completion of your energy saving works, we are contacting you on behalf of {{SCHEME_NAME}} to arrange the booking of an Energy Performance Certificate (EPC) for',
    duration: 'The assessment will take around 30 minutes'
  },
  POST_QI: {
    id: 'POST_QI',
    label: 'Post installation quality inspection',
    body: 'following the completion of your energy saving works, we are contacting you on behalf of {{SCHEME_NAME}} to arrange the booking of a quality inspection for',
    duration: 'The assessment will take around 30 minutes'
  }
};

const SCHEME_OPTIONS = [
  {
    id: 'MTVH',
    label: 'MTVH',
    schemeName: 'your landlord, Metropolitan Thames Valley Housing (MTVH)',
    company: 'Happy Energy Solutions',
    schemeNumber: '0115 784 3060'
  },
  {
    id: 'ESSEX',
    label: 'Essex CC',
    schemeName: 'the Essex County Council Warm Homes Local Grant service',
    company: 'Heat Essex',
    schemeNumber: '01206 326808'
  }
];

const ASSESSOR_OPTIONS = [
  'Daniel Cotter',
  'Chris Humphries',
  'Chris O\'Halloran',
  'Simon Peterson'
];

const TIME_OPTIONS = {
  AM: {
    id: 'AM',
    label: 'AM',
    timeText: 'on the morning of',
    timeDetail: 'between 8.30am and 1pm'
  },
  PM: {
    id: 'PM',
    label: 'PM',
    timeText: 'on the afternoon of',
    timeDetail: 'between 1pm and 4.30pm'
  },
  ALL_DAY: {
    id: 'ALL_DAY',
    label: 'All Day',
    timeText: 'on the',
    timeDetail: 'any time between 8.30am and 4.30pm'
  }
};

const DEFAULT_INVALID_RESPONSE = 'Apologies but this texting service can only respond to Yes, No, Call, or Text. Please respond with one of these 4 options, or please feel free to call us on {{SCHEME_NUMBER}} between 9am-5pm Monday to Friday or email us at info@happyenergy.co.uk. A team member will also check your message and respond if we do not hear from you.';
const DEFAULT_YES_RESPONSE = 'Thanks for confirming the appointment, please make sure to add it to your calendar.';
const DEFAULT_CALL_RESPONSE = 'Thanks for requesting a call, we will contact you on this number to discuss when might be convenient, if you want us to call a different number please text that to us now, or please feel free to call us on 0115 784 3060 between 9am-5pm Monday to Friday.';
const DEFAULT_TEXT_RESPONSE = 'Ok, thanks for confirming you wish to text with one of our team members, someone will respond shortly, but in the meantime, please feel free to send us a further message, such as any preferred dates, or relevant information, to allow our team member to be ready to respond';
const DEFAULT_NO_FINAL_RESPONSE = 'Ok thanks for confirming that the appointment was not convenient, we will call you to discuss when might be convenient, or please feel free to call us on {{SCHEME_NUMBER}} between 9am-5pm Monday to Friday.';
const MANAGED_TEMPLATE_NAME_PREFIX = 'Karma Text Booking Flow v2';

const firstValue = (value) => {
  if (Array.isArray(value)) {
    return value[0] || '';
  }
  return value || '';
};

const contactLabel = (contact) => {
  if (!contact) {
    return '';
  }
  const first = firstValue(contact.first_name || contact.firstName).trim();
  const last = firstValue(contact.last_name || contact.lastName).trim();
  const full = `${first} ${last}`.trim();
  return full || firstValue(contact.label) || `Contact ${contact.id}`;
};

const getAddressCandidates = (contact) => {
  if (!contact || typeof contact !== 'object') {
    return [];
  }

  if (Array.isArray(contact.addresses)) {
    return contact.addresses.filter(Boolean);
  }

  if (contact.addresses && typeof contact.addresses === 'object') {
    return [contact.addresses];
  }

  if (contact.address && typeof contact.address === 'object') {
    return [contact.address];
  }

  return [];
};

const getAddressPreview = (contact) => {
  const candidates = getAddressCandidates(contact);

  for (const candidate of candidates) {
    const street = firstValue(candidate?.street || candidate?.line1 || candidate?.address1 || candidate?.address_line_1).trim();
    const postcode = firstValue(candidate?.postal_code || candidate?.postcode || candidate?.zip).trim();
    const preview = [street, postcode].filter(Boolean).join(', ').trim();
    if (preview) {
      return preview;
    }
  }

  const flatStreet = firstValue(contact?.street || contact?.address_line_1 || contact?.address1).trim();
  const flatPostcode = firstValue(contact?.postal_code || contact?.postcode || contact?.zip).trim();
  const flatPreview = [flatStreet, flatPostcode].filter(Boolean).join(', ').trim();
  if (flatPreview) {
    return flatPreview;
  }

  return firstValue(contact?.subtitle || contact?.description || contact?.secondary_text).trim();
};

const getPhoneOptions = (contact) => {
  if (!contact) {
    return [];
  }

  const phones = Array.isArray(contact.phone_numbers)
    ? contact.phone_numbers
    : Array.isArray(contact.phoneNumbers)
      ? contact.phoneNumbers
      : [];

  return phones
    .map((item, idx) => {
      const number = String(item?.number || '').trim();
      if (!number) {
        return null;
      }
      const tag = item?.label || item?.type || `Phone ${idx + 1}`;
      return {
        key: `${idx}-${number}`,
        label: `${tag}: ${number}`,
        number
      };
    })
    .filter(Boolean);
};

const getPhoneCount = (contact) => {
  if (!contact || typeof contact !== 'object') {
    return null;
  }

  const phones = Array.isArray(contact.phone_numbers)
    ? contact.phone_numbers
    : Array.isArray(contact.phoneNumbers)
      ? contact.phoneNumbers
      : null;

  if (!phones) {
    return null;
  }

  return phones.filter((item) => String(item?.number || '').trim()).length;
};

const formatSearchMeta = (option) => {
  const parts = [option?.address || 'No address'];
  if (Number.isInteger(option?.phoneCount)) {
    parts.push(`${option.phoneCount} ${option.phoneCount === 1 ? 'number' : 'numbers'}`);
  }
  return parts.join(' | ');
};

const pickStreet = (contact) => {
  const candidates = getAddressCandidates(contact);
  for (const candidate of candidates) {
    const street = firstValue(candidate?.street || candidate?.line1 || candidate?.address1 || candidate?.address_line_1).trim();
    if (street) {
      return street;
    }
  }
  return firstValue(contact?.street || contact?.address_line_1 || contact?.address1).trim();
};

const formatDateOrdinal = (dayNumber) => {
  const remainder10 = dayNumber % 10;
  const remainder100 = dayNumber % 100;

  if (remainder10 === 1 && remainder100 !== 11) {
    return `${dayNumber}st`;
  }

  if (remainder10 === 2 && remainder100 !== 12) {
    return `${dayNumber}nd`;
  }

  if (remainder10 === 3 && remainder100 !== 13) {
    return `${dayNumber}rd`;
  }

  return `${dayNumber}th`;
};

const formatFullDate = (dateValue) => {
  const raw = String(dateValue || '').trim();
  if (!raw) {
    return '';
  }

  const parsed = new Date(`${raw}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }

  const weekday = new Intl.DateTimeFormat('en-GB', { weekday: 'long' }).format(parsed);
  const month = new Intl.DateTimeFormat('en-GB', { month: 'long' }).format(parsed);
  const day = formatDateOrdinal(parsed.getDate());
  const year = parsed.getFullYear();
  return `${weekday} the ${day} of ${month} ${year}`;
};

const fillTemplate = (template, replacements) => {
  let output = String(template || '');
  Object.entries(replacements || {}).forEach(([key, value]) => {
    output = output.replaceAll(`{{${key}}}`, String(value ?? ''));
  });
  return output;
};

const normalizeSmsText = (value) => {
  const raw = String(value || '');
  return raw
    .replace(/\r\n/g, '\n')
    .replace(/\t+/g, ' ')
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const formatSlotPhrase = (dateText, timeOptionId) => {
  const timeOption = TIME_OPTIONS[timeOptionId] || TIME_OPTIONS.AM;
  const date = String(dateText || '').trim();
  if (!date) {
    return '';
  }
  return {
    date,
    dateDisplay: formatFullDate(date),
    time: timeOption.label,
    timeText: timeOption.timeText,
    timeDetail: timeOption.timeDetail
  };
};

const defaultPhoneFromOptions = (phoneOptions) => {
  if (!Array.isArray(phoneOptions) || phoneOptions.length === 0) {
    return '';
  }
  const mobileLike = phoneOptions.find((item) => /^\+?447|^07/.test(item.number));
  return (mobileLike || phoneOptions[0]).number;
};

const normalizeSearchResults = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.list)) {
    return payload.list;
  }

  if (Array.isArray(payload?.results)) {
    return payload.results;
  }

  return [];
};

const getManagedTemplateName = (appointmentTypeId, appointmentTypes = Object.values(APPOINTMENT_TYPES)) => {
  const appointment = appointmentTypes.find((t) => t.id === appointmentTypeId) || appointmentTypes[0] || APPOINTMENT_TYPES.WHA;
  return `${MANAGED_TEMPLATE_NAME_PREFIX} - ${appointment.label}`;
};

const EDITABLE_TEXT_CONTROL_STYLE = {
  backgroundColor: '#f5f7fa',
  borderColor: '#d6dde6'
};

const KarmaTextBooking = () => {
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount() || instance.getAllAccounts?.()[0] || null;
  const isSmsAdmin = isSmsAdminAccount(activeAccount);
  const [isSearching, setIsSearching] = useState(false);
  const [searchOptions, setSearchOptions] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [loadingContact, setLoadingContact] = useState(false);
  const [selectedPhone, setSelectedPhone] = useState('');
  const [appointmentType, setAppointmentType] = useState('WHA');
  const [schemeId, setSchemeId] = useState('MTVH');
  const [assessor, setAssessor] = useState(() => {
    try {
      const stored = localStorage.getItem('karmaBookingOptions');
      if (stored) {
        const opts = JSON.parse(stored);
        return opts.assessorOptions?.[0] || ASSESSOR_OPTIONS[0];
      }
    } catch {}
    return ASSESSOR_OPTIONS[0];
  });
  const [date1, setDate1] = useState('');
  const [time1, setTime1] = useState('AM');
  const [date2, setDate2] = useState('');
  const [time2, setTime2] = useState('AM');
  const [date3, setDate3] = useState('');
  const [time3, setTime3] = useState('AM');
  const [outboundText, setOutboundText] = useState('');
  const [yesResponse, setYesResponse] = useState(DEFAULT_YES_RESPONSE);
  const [callResponse, setCallResponse] = useState(DEFAULT_CALL_RESPONSE);
  const [textResponse, setTextResponse] = useState(DEFAULT_TEXT_RESPONSE);
  const [invalidResponse, setInvalidResponse] = useState(DEFAULT_INVALID_RESPONSE);
  const [noFinalResponse, setNoFinalResponse] = useState(DEFAULT_NO_FINAL_RESPONSE);
  const [offer2Text, setOffer2Text] = useState('');
  const [offer3Text, setOffer3Text] = useState('');
  const [sending, setSending] = useState(false);
  const [alert, setAlert] = useState({ type: '', message: '' });
  const [contactSearchResetKey, setContactSearchResetKey] = useState(0);

  const [bookingOptions, setBookingOptions] = useState(() => {
    try {
      const stored = localStorage.getItem('karmaBookingOptions');
      if (stored) return JSON.parse(stored);
    } catch {}
    return {
      appointmentTypes: Object.values(APPOINTMENT_TYPES),
      schemeOptions: SCHEME_OPTIONS,
      assessorOptions: ASSESSOR_OPTIONS
    };
  });
  // Shared, centrally-stored message wording. Falls back to built-in defaults
  // if it cannot be loaded, so booking always keeps working.
  const [messageConfig, setMessageConfig] = useState(() => messageConfigService.withDefaults(null));

  // Shared booking options (assessors / schemes / appointment types).
  const [optionsStatus, setOptionsStatus] = useState({
    loading: true, saving: false, source: 'local', updatedAt: null, updatedBy: null, error: null, dirty: false
  });

  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [optionsSection, setOptionsSection] = useState('assessors');
  const [editingItem, setEditingItem] = useState(null);

  const markOptionsDirty = useCallback(() => {
    setOptionsStatus((prev) => (prev.loading || prev.dirty ? prev : { ...prev, dirty: true }));
  }, []);

  const ensureManagedTemplate = useCallback(async (appointmentTypeId) => {
    const targetName = getManagedTemplateName(appointmentTypeId, bookingOptions.appointmentTypes);
    const templatesResponse = await smsApiService.getTemplates(instance);
    const templates = Array.isArray(templatesResponse?.data) ? templatesResponse.data : [];
    const existing = templates.find((template) => String(template?.Name || template?.name || '').trim() === targetName);
    if (existing?.Id || existing?.id) {
      return Number.parseInt(existing.Id || existing.id, 10);
    }

    throw new Error(`Booking template "${targetName}" was not found. Open SMS Template Builder and create or update this template first.`);
  }, [instance, bookingOptions.appointmentTypes]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { config } = await messageConfigService.loadConfig(instance);
      if (!cancelled) setMessageConfig(config);
    })();
    return () => { cancelled = true; };
  }, [instance]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await bookingOptionsService.loadOptions(instance, {
        appointmentTypes: Object.values(APPOINTMENT_TYPES),
        schemeOptions: SCHEME_OPTIONS,
        assessorOptions: ASSESSOR_OPTIONS
      });
      if (cancelled) return;
      setBookingOptions(result.options);
      setOptionsStatus({
        loading: false,
        saving: false,
        source: result.source,
        updatedAt: result.updatedAt,
        updatedBy: result.updatedBy,
        error: result.error,
        dirty: false
      });
    })();
    return () => { cancelled = true; };
  }, [instance]);

  // Kept as a local cache only. Publishing to all users is an explicit action
  // via "Save for everyone" in Manage Booking Options.
  useEffect(() => {
    localStorage.setItem('karmaBookingOptions', JSON.stringify(bookingOptions));
  }, [bookingOptions]);

  const publishBookingOptions = useCallback(async () => {
    setOptionsStatus((prev) => ({ ...prev, saving: true, error: null }));
    const result = await bookingOptionsService.saveOptions(
      bookingOptions,
      instance,
      activeAccount?.username || 'unknown'
    );
    setOptionsStatus((prev) => ({
      ...prev,
      saving: false,
      source: result.ok ? 'shared' : prev.source,
      updatedAt: result.ok ? result.updatedAt : prev.updatedAt,
      updatedBy: result.ok ? (activeAccount?.username || 'unknown') : prev.updatedBy,
      dirty: result.ok ? false : prev.dirty,
      error: result.error
    }));
  }, [bookingOptions, instance, activeAccount]);

  useEffect(() => {
    setYesResponse(DEFAULT_YES_RESPONSE);
    setCallResponse(DEFAULT_CALL_RESPONSE);
    setTextResponse(DEFAULT_TEXT_RESPONSE);
    setInvalidResponse(DEFAULT_INVALID_RESPONSE);
    setNoFinalResponse(DEFAULT_NO_FINAL_RESPONSE);
  }, []);

  const handleSearch = useCallback(async (query) => {
    const trimmed = String(query || '').trim();
    if (!trimmed) {
      setSearchOptions([]);
      return;
    }

    setIsSearching(true);

    try {
      let combined = [];
      const [street, postcode, sitewide] = await Promise.all([
        KarmaApiService.contactsFiltered({ queries: `&filters[addresses][street]=${trimmed}` }, 1, 100, instance),
        KarmaApiService.contactsFiltered({ queries: `&filters[addresses][postal_code]=${trimmed}` }, 1, 100, instance),
        KarmaApiService.siteWideSearch(trimmed, instance)
      ]);

      combined = combined.concat(normalizeSearchResults(street?.data));
      combined = combined.concat(normalizeSearchResults(postcode?.data));
      combined = combined.concat(normalizeSearchResults(sitewide?.data));

      const dedupe = new Map();
      combined.forEach((item) => {
        const first = firstValue(item?.first_name).trim();
        const last = firstValue(item?.last_name).trim();
        const label = `${first} ${last}`.trim() || firstValue(item?.label).trim();
        const addressPreview = getAddressPreview(item);
        const dedupeKey = item?.id || [label.toLowerCase(), addressPreview.toLowerCase()].join('|');

        if (dedupeKey && !dedupe.has(dedupeKey)) {
          dedupe.set(dedupeKey, item);
        }
      });

      const options = Array.from(dedupe.values()).map((item) => {
        const first = firstValue(item.first_name);
        const last = firstValue(item.last_name);
        const addressPreview = getAddressPreview(item);
        const phoneCount = getPhoneCount(item);

        return {
          id: item.id,
          label: `${first} ${last}`.trim() || firstValue(item.label) || `Contact ${item.id}`,
          address: addressPreview,
          phoneCount
        };
      });

      const missingDetailIds = options
        .filter((option) => (!String(option.address || '').trim() || !Number.isInteger(option.phoneCount)) && option.id)
        .slice(0, 10)
        .map((option) => option.id);

      if (missingDetailIds.length > 0) {
        const detailResults = await Promise.allSettled(
          missingDetailIds.map((id) => KarmaApiService.contactGet(id, instance))
        );

        const addressById = new Map();
        const phoneCountById = new Map();
        detailResults.forEach((result) => {
          if (result.status !== 'fulfilled') {
            return;
          }

          const fullContact = result.value?.data;
          const id = fullContact?.id;
          const address = getAddressPreview(fullContact);
          const phoneCount = getPhoneCount(fullContact);
          if (id && address) {
            addressById.set(id, address);
            addressById.set(String(id), address);
          }

          if (id && Number.isInteger(phoneCount)) {
            phoneCountById.set(id, phoneCount);
            phoneCountById.set(String(id), phoneCount);
          }
        });

        setSearchOptions(
          options.map((option) => ({
            ...option,
            address: option.address || addressById.get(option.id) || addressById.get(String(option.id)) || '',
            phoneCount: Number.isInteger(option.phoneCount)
              ? option.phoneCount
              : (phoneCountById.get(option.id) ?? phoneCountById.get(String(option.id)) ?? null)
          }))
        );
      } else {
        setSearchOptions(options);
      }
    } catch (error) {
      setAlert({ type: 'danger', message: 'Contact search failed.' });
    } finally {
      setIsSearching(false);
    }
  }, [instance]);

  const handleSelectContact = useCallback(async (selection) => {
    const selected = selection?.[0];
    if (!selected?.id) {
      setSelectedContact(null);
      setSelectedPhone('');
      return;
    }

    setLoadingContact(true);
    setAlert({ type: '', message: '' });

    try {
      const response = await KarmaApiService.contactGet(selected.id, instance);
      const contact = response?.data;
      const phoneOptions = getPhoneOptions(contact);
      setSelectedContact(contact || null);
      setSelectedPhone(defaultPhoneFromOptions(phoneOptions));
    } catch (error) {
      setSelectedContact(null);
      setSelectedPhone('');
      setAlert({ type: 'danger', message: 'Unable to load contact details.' });
    } finally {
      setLoadingContact(false);
    }
  }, [instance]);

  const phoneOptions = useMemo(() => getPhoneOptions(selectedContact), [selectedContact]);

  const selectedScheme = useMemo(() => {
    return bookingOptions.schemeOptions.find((item) => item.id === schemeId) || bookingOptions.schemeOptions[0] || SCHEME_OPTIONS[0];
  }, [schemeId, bookingOptions.schemeOptions]);

  const currentAppointmentType = useMemo(() => {
    return bookingOptions.appointmentTypes.find((t) => t.id === appointmentType) || bookingOptions.appointmentTypes[0] || APPOINTMENT_TYPES.WHA;
  }, [appointmentType, bookingOptions.appointmentTypes]);

  const updateSelectedSchemeField = useCallback((field, value) => {
    markOptionsDirty(); setBookingOptions((prev) => ({
      ...prev,
      schemeOptions: prev.schemeOptions.map((item) => (
        item.id === schemeId ? { ...item, [field]: value } : item
      ))
    }));
  }, [schemeId]);

  const updateSelectedAppointmentField = useCallback((field, value) => {
    markOptionsDirty(); setBookingOptions((prev) => ({
      ...prev,
      appointmentTypes: prev.appointmentTypes.map((item) => (
        item.id === appointmentType ? { ...item, [field]: value } : item
      ))
    }));
  }, [appointmentType]);

  const customerTitle = useMemo(() => {
    const fromPosition = firstValue(selectedContact?.position).trim();
    if (fromPosition) {
      return fromPosition;
    }
    return 'Customer';
  }, [selectedContact]);

  const customerLastName = useMemo(() => {
    return firstValue(selectedContact?.last_name || selectedContact?.lastName).trim() || 'Resident';
  }, [selectedContact]);

  const slot1 = useMemo(() => formatSlotPhrase(date1, time1), [date1, time1]);
  const slot2 = useMemo(() => formatSlotPhrase(date2, time2), [date2, time2]);
  const slot3 = useMemo(() => formatSlotPhrase(date3, time3), [date3, time3]);

  const placeholderReference = useMemo(() => {
    const street = pickStreet(selectedContact) || '';
    return [
      { token: '{{TITLE}}', description: 'Contact title from Karma', value: customerTitle || '' },
      { token: '{{LAST_NAME}}', description: 'Contact surname from Karma', value: customerLastName || '' },
      { token: '{{STREET_H}}', description: 'Contact street from Karma', value: street },
      { token: '{{COMPANY}}', description: 'Selected scheme company text', value: selectedScheme?.company || '' },
      { token: '{{SCHEME_NAME}}', description: 'Selected scheme name text', value: selectedScheme?.schemeName || '' },
      { token: '{{SCHEME_NUMBER}}', description: 'Selected scheme phone number', value: selectedScheme?.schemeNumber || '' },
      { token: '{{ASSESSOR}}', description: 'Selected assessor', value: assessor || '' },
      { token: '{{DATE}}', description: 'Primary appointment date', value: slot1.dateDisplay || '' },
      { token: '{{TIME_TEXT}}', description: 'Primary slot phrase', value: slot1.timeText || '' },
      { token: '{{TIME}}', description: 'Primary slot label', value: slot1.time || '' },
      { token: '{{TIME_DETAIL}}', description: 'Primary slot time range', value: slot1.timeDetail || '' },
      { token: '{{OUTBOUND_TEXT}}', description: 'Outbound SMS content block', value: '[editable below]' },
      { token: '{{YES_RESPONSE}}', description: 'Inbound SMS response for "Yes"', value: '[editable below]' },
      { token: '{{NO_FINAL_RESPONSE}}', description: 'Inbound SMS response for "No" final', value: '[editable below]' },
      { token: '{{CALL_RESPONSE}}', description: 'Inbound SMS response for "Call"', value: '[editable below]' },
      { token: '{{TEXT_RESPONSE}}', description: 'Inbound SMS response for "Text"', value: '[editable below]' },
      { token: '{{INVALID_RESPONSE}}', description: 'Inbound SMS response for other replies', value: '[editable below]' }
    ];
  }, [selectedContact, customerTitle, customerLastName, selectedScheme, assessor, slot1.dateDisplay, slot1.timeText, slot1.time, slot1.timeDetail]);

  useEffect(() => {
    const street = pickStreet(selectedContact) || '{{STREET_H}}';
    const replacements = {
      SCHEME_NAME: selectedScheme.schemeName,
      SCHEME_NUMBER: selectedScheme.schemeNumber,
      COMPANY: selectedScheme.company,
      TITLE: customerTitle,
      LAST_NAME: customerLastName,
      STREET_H: street,
      DATE: slot1.dateDisplay || '{{DATE}}',
      TIME_TEXT: slot1.timeText || '{{TIME_TEXT}}',
      TIME: slot1.time || '{{TIME}}',
      TIME_DETAIL: slot1.timeDetail || '{{TIME_DETAIL}}',
      ASSESSOR: assessor
    };

    const cfg = messageConfigService.withDefaults(messageConfig);
    const sharedReplacements = { ...replacements, OFFICE_HOURS: cfg.shared.officeHours };

    // Wording comes from the shared configuration (SMS Workspace > Message Text),
    // not from this file. Editing it there changes it for every user.
    const builtOutbound = messageConfigService.buildOutboundTemplate(cfg, currentAppointmentType.id);
    const offerTemplate = messageConfigService.buildOfferTemplate(cfg);

    const builtOffer2 = slot2.date
      ? fillTemplate(offerTemplate, {
          ...sharedReplacements,
          DATE: slot2.dateDisplay || '{{DATE}}',
          TIME_TEXT: slot2.timeText || '{{TIME_TEXT}}',
          TIME: slot2.time || '{{TIME}}',
          TIME_DETAIL: slot2.timeDetail || '{{TIME_DETAIL}}'
        })
      : '';

    const builtOffer3 = slot3.date
      ? fillTemplate(offerTemplate, {
          ...sharedReplacements,
          DATE: slot3.dateDisplay || '{{DATE}}',
          TIME_TEXT: slot3.timeText || '{{TIME_TEXT}}',
          TIME: slot3.time || '{{TIME}}',
          TIME_DETAIL: slot3.timeDetail || '{{TIME_DETAIL}}'
        })
      : '';

    setOutboundText(normalizeSmsText(fillTemplate(builtOutbound, sharedReplacements)));
    setOffer2Text(normalizeSmsText(builtOffer2));
    setOffer3Text(normalizeSmsText(builtOffer3));
    setYesResponse(normalizeSmsText(fillTemplate(cfg.shared.yesResponse, sharedReplacements)));
    setCallResponse(normalizeSmsText(fillTemplate(cfg.shared.callResponse, sharedReplacements)));
    setTextResponse(normalizeSmsText(fillTemplate(cfg.shared.textResponse, sharedReplacements)));
    setInvalidResponse(normalizeSmsText(fillTemplate(cfg.shared.invalidResponse, sharedReplacements)));
    setNoFinalResponse(normalizeSmsText(fillTemplate(cfg.shared.noFinalResponse, sharedReplacements)));
  }, [selectedContact, selectedScheme, currentAppointmentType, assessor, slot1.date, slot1.time, slot1.timeText, slot1.timeDetail, slot2.date, slot2.timeText, slot2.timeDetail, slot3.date, slot3.timeText, slot3.timeDetail, customerTitle, customerLastName, messageConfig]);

  // Block sending if any placeholder is still unresolved: a literal "{{STREET_H}}"
  // must never reach a customer.
  const messageValidation = useMemo(() => messageConfigService.validateMessages({
    'Outbound message': outboundText,
    'Second date offer': offer2Text,
    'Third date offer': offer3Text,
    'Yes reply': yesResponse,
    'No reply': noFinalResponse,
    'Call reply': callResponse,
    'Text reply': textResponse,
    'Unrecognised reply': invalidResponse
  }), [outboundText, offer2Text, offer3Text, yesResponse, noFinalResponse, callResponse, textResponse, invalidResponse]);

  const handleStartBooking = useCallback(async () => {
    if (!selectedContact?.id) {
      setAlert({ type: 'warning', message: 'Select a contact first.' });
      return;
    }

    if (!selectedPhone.trim()) {
      setAlert({ type: 'warning', message: 'Phone number is required.' });
      return;
    }

    if (!String(date1 || '').trim()) {
      setAlert({ type: 'warning', message: 'Appointment Date is required.' });
      return;
    }

    if (!messageValidation.ok) {
      setAlert({
        type: 'danger',
        message: `Cannot send: ${messageValidation.tokens.join(', ')} `
          + `${messageValidation.tokens.length === 1 ? 'has' : 'have'} no value, so `
          + `${messageValidation.tokens.length === 1 ? 'it' : 'they'} would be sent to the customer as text. `
          + 'See the details above the Start Text Booking button.'
      });
      return;
    }

    setSending(true);
    setAlert({ type: '', message: '' });

    try {
      const managedTemplateId = await ensureManagedTemplate(currentAppointmentType.id);

      const contextData = {
        firstName: firstValue(selectedContact.first_name),
        lastName: firstValue(selectedContact.last_name),
        title: customerTitle,
        customerName: contactLabel(selectedContact),
        streetH: pickStreet(selectedContact),
        fullName: contactLabel(selectedContact),
        contactId: selectedContact.id,
        company: selectedScheme.company,
        schemeName: selectedScheme.schemeName,
        schemeNumber: selectedScheme.schemeNumber,
        appointmentType: currentAppointmentType.label,
        assessor,
        appointment1Date: slot1.dateDisplay || '',
        appointment1Time: slot1.time || '',
        appointment2Date: slot2.dateDisplay || '',
        appointment2Time: slot2.time || '',
        appointment3Date: slot3.dateDisplay || '',
        appointment3Time: slot3.time || '',
        outboundText,
        offer2Text,
        offer3Text,
        yesResponse,
        callResponse,
        textResponse,
        invalidResponse,
        noFinalResponse,
        OUTBOUND_TEXT: outboundText,
        OFFER2_TEXT: offer2Text,
        OFFER3_TEXT: offer3Text,
        YES_RESPONSE: yesResponse,
        CALL_RESPONSE: callResponse,
        TEXT_RESPONSE: textResponse,
        INVALID_RESPONSE: invalidResponse,
        NO_FINAL_RESPONSE: noFinalResponse,
        source: 'karma-text-booking',
        outboundPreview: outboundText
      };

      const startResponse = await smsApiService.startConversation(
        selectedPhone.trim(),
        Number.parseInt(managedTemplateId, 10),
        contextData,
        instance,
        {}
      );

      const conversationId = startResponse?.data?.conversationId;

      // Karma note is written automatically by the backend's sendSMSMessage helper — no duplicate needed here.

      // Reset booking UI so operator can immediately search and send the next contact.
      setSearchOptions([]);
      setSelectedContact(null);
      setSelectedPhone('');
      setDate1('');
      setTime1('AM');
      setDate2('');
      setTime2('AM');
      setDate3('');
      setTime3('AM');
      setContactSearchResetKey((current) => current + 1);

      setAlert({ type: 'success', message: `Booking SMS started${conversationId ? ` (Conversation ${conversationId})` : ''}.` });
    } catch (error) {
      const message = error?.response?.data?.error || error?.response?.data?.details || 'Failed to start booking SMS.';
      setAlert({ type: 'danger', message });
    } finally {
      setSending(false);
    }
  }, [assessor, callResponse, messageValidation, currentAppointmentType.id, currentAppointmentType.label, customerTitle, date1, date2, date3, ensureManagedTemplate, instance, invalidResponse, noFinalResponse, offer2Text, offer3Text, outboundText, selectedContact, selectedPhone, selectedScheme.company, selectedScheme.schemeName, selectedScheme.schemeNumber, slot1.date, slot1.time, slot2.date, slot2.time, slot3.date, slot3.time, textResponse, yesResponse]);

  return (
    <>
    <Card className="border-0 shadow-sm">
      <Card.Body>
        <Row className="mb-3">
          <Col>
            <h5 className="mb-1">Karma Text Booking</h5>
            <div className="text-muted small">
              {isSmsAdmin
                ? 'Build and send appointment booking SMS flows from Karma with editable outbound and response text.'
                : 'Look up a customer, set appointment dates/times, and send the booking SMS using admin-managed templates.'}
            </div>
          </Col>
        </Row>

        {alert.message && (
          <Alert variant={alert.type || 'info'}>{alert.message}</Alert>
        )}

        {isSmsAdmin ? (
        <Card className="mb-3 border">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <span>Admin Section</span>
            <Button size="sm" variant="outline-secondary" onClick={() => { setOptionsSection('assessors'); setEditingItem(null); setShowOptionsModal(true); }} title="Manage dropdown options">
              ⚙ Manage Options
            </Button>
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Holding Text: {'{{SCHEME_NAME}}'}</Form.Label>
                  <Form.Control
                    value={selectedScheme?.schemeName || ''}
                    onChange={(event) => updateSelectedSchemeField('schemeName', event.target.value)}
                    placeholder="Scheme name used in SMS text"
                    style={EDITABLE_TEXT_CONTROL_STYLE}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Holding Text: {'{{COMPANY}}'}</Form.Label>
                  <Form.Control
                    value={selectedScheme?.company || ''}
                    onChange={(event) => updateSelectedSchemeField('company', event.target.value)}
                    placeholder="Company name"
                    style={EDITABLE_TEXT_CONTROL_STYLE}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Holding Text: {'{{SCHEME_NUMBER}}'}</Form.Label>
                  <Form.Control
                    value={selectedScheme?.schemeNumber || ''}
                    onChange={(event) => updateSelectedSchemeField('schemeNumber', event.target.value)}
                    placeholder="Phone number"
                    style={EDITABLE_TEXT_CONTROL_STYLE}
                  />
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group>
                  <Form.Label>Holding Text: Appointment Body (for selected appointment type)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={currentAppointmentType?.body || ''}
                    onChange={(event) => updateSelectedAppointmentField('body', event.target.value)}
                    placeholder="Main appointment sentence used in outbound SMS"
                    style={EDITABLE_TEXT_CONTROL_STYLE}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Holding Text: Appointment Duration</Form.Label>
                  <Form.Control
                    value={currentAppointmentType?.duration || ''}
                    onChange={(event) => updateSelectedAppointmentField('duration', event.target.value)}
                    placeholder="Duration sentence"
                    style={EDITABLE_TEXT_CONTROL_STYLE}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Alert variant="light" className="mt-3 mb-0 border">
              <div className="fw-semibold mb-2">Placeholder Reference (values currently used in this SMS)</div>
              <Row className="g-1">
                {placeholderReference.map((item) => (
                  <Col md={6} key={item.token}>
                    <div className="small">
                      <span className="text-danger">{item.token}</span>
                      <span className="text-muted"> - {item.description}</span>
                      <div className="text-muted">Current value: "{item.value || '[empty]'}"</div>
                    </div>
                  </Col>
                ))}
              </Row>
              <div className="small text-muted mt-2">
                Note: {'{{preferredDayTime}}'} is not set automatically in this booking screen. It only works if another workflow passes it in context data.
              </div>
              <div className="small text-muted mt-1">
                Template stage text and most placeholder usage are managed in SMS Template Builder.
              </div>
            </Alert>
          </Card.Body>
        </Card>
        ) : (
        <Alert variant="light" className="mb-3 border">
          <div className="fw-semibold mb-1">Simple Operator View</div>
          <div className="small text-muted">Template wording and placeholder mappings are managed by SMS admins. Enter customer + appointment details below.</div>
        </Alert>
        )}

        <Row className="g-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Appointment Type</Form.Label>
              <Form.Select value={appointmentType} onChange={(event) => setAppointmentType(event.target.value)}>
                {bookingOptions.appointmentTypes.map((item) => (
                  <option key={item.id} value={item.id}>{item.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Scheme</Form.Label>
              <Form.Select value={schemeId} onChange={(event) => setSchemeId(event.target.value)}>
                {bookingOptions.schemeOptions.map((item) => (
                  <option key={item.id} value={item.id}>{item.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Assessor</Form.Label>
              <Form.Select value={assessor} onChange={(event) => setAssessor(event.target.value)}>
                {bookingOptions.assessorOptions.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <Row className="g-3 mt-2">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Contact Search</Form.Label>
              <AsyncTypeahead
                key={contactSearchResetKey}
                id="karma-text-booking-contact-search"
                minLength={3}
                filterBy={() => true}
                inputProps={{ style: EDITABLE_TEXT_CONTROL_STYLE }}
                isLoading={isSearching}
                onSearch={handleSearch}
                options={searchOptions}
                labelKey="label"
                onChange={handleSelectContact}
                placeholder="Search by name, street, or postcode"
                renderMenuItemChildren={(option) => (
                  <>
                    <div>{option.label}</div>
                    <small className="text-muted">{formatSearchMeta(option)}</small>
                  </>
                )}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Select Phone Number</Form.Label>
              {phoneOptions.length > 0 ? (
                <Form.Select value={selectedPhone} onChange={(event) => setSelectedPhone(event.target.value)}>
                  {phoneOptions.map((phone) => (
                    <option key={phone.key} value={phone.number}>
                      {phone.label}
                    </option>
                  ))}
                </Form.Select>
              ) : (
                <Form.Control
                  value={selectedPhone}
                  onChange={(event) => setSelectedPhone(event.target.value)}
                  placeholder="Enter a phone number"
                  style={EDITABLE_TEXT_CONTROL_STYLE}
                />
              )}
              <Form.Control
                className="mt-2"
                value={selectedPhone}
                onChange={(event) => setSelectedPhone(event.target.value)}
                placeholder="Manual override (optional)"
                style={EDITABLE_TEXT_CONTROL_STYLE}
              />
              <Form.Text className="text-muted">Choose a phone number from the list, or edit it manually below.</Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <Card className="mt-3 mb-3 border">
          <Card.Header>Appointments</Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Date 1</Form.Label>
                  <Form.Control type="date" value={date1} onChange={(event) => setDate1(event.target.value)} style={EDITABLE_TEXT_CONTROL_STYLE} />
                  <Form.Text className="text-muted">{formatFullDate(date1) || 'Select a date to preview the full day and date.'}</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Time 1</Form.Label>
                  <Form.Select value={time1} onChange={(event) => setTime1(event.target.value)}>
                    {Object.values(TIME_OPTIONS).map((item) => (
                      <option key={item.id} value={item.id}>{item.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Optional Date 2</Form.Label>
                  <Form.Control type="date" value={date2} onChange={(event) => setDate2(event.target.value)} style={EDITABLE_TEXT_CONTROL_STYLE} />
                  <Form.Text className="text-muted">{formatFullDate(date2) || 'Optional date preview will appear here.'}</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Optional Time 2</Form.Label>
                  <Form.Select value={time2} onChange={(event) => setTime2(event.target.value)}>
                    {Object.values(TIME_OPTIONS).map((item) => (
                      <option key={item.id} value={item.id}>{item.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Optional Date 3</Form.Label>
                  <Form.Control type="date" value={date3} onChange={(event) => setDate3(event.target.value)} style={EDITABLE_TEXT_CONTROL_STYLE} />
                  <Form.Text className="text-muted">{formatFullDate(date3) || 'Optional date preview will appear here.'}</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Optional Time 3</Form.Label>
                  <Form.Select value={time3} onChange={(event) => setTime3(event.target.value)}>
                    {Object.values(TIME_OPTIONS).map((item) => (
                      <option key={item.id} value={item.id}>{item.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Row className="g-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Customer Name to Use</Form.Label>
              <Form.Control value={`${customerTitle} ${customerLastName}`.trim()} disabled />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Customer Mobile from Karma</Form.Label>
              <Form.Control
                value={selectedPhone}
                disabled
                placeholder="No phone selected"
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Street (h)</Form.Label>
              <Form.Control value={pickStreet(selectedContact)} disabled placeholder="Auto from selected Karma record" />
            </Form.Group>
          </Col>
        </Row>

        <Card className="mt-3 mb-3 border">
          <Card.Header>{isSmsAdmin ? 'Editable SMS Content' : 'SMS Content Summary'}</Card.Header>
          <Card.Body>
            {isSmsAdmin ? (
            <Row className="g-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Outbound SMS Text <span className="text-danger">{'{{OUTBOUND_TEXT}}'}</span></Form.Label>
                  <Form.Control as="textarea" rows={8} value={outboundText} onChange={(event) => setOutboundText(event.target.value)} style={EDITABLE_TEXT_CONTROL_STYLE} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Inbound SMS Response: "Yes" <span className="text-danger">{'{{YES_RESPONSE}}'}</span></Form.Label>
                  <Form.Control as="textarea" rows={4} value={yesResponse} onChange={(event) => setYesResponse(event.target.value)} style={EDITABLE_TEXT_CONTROL_STYLE} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Inbound SMS Response: "Call" <span className="text-danger">{'{{CALL_RESPONSE}}'}</span></Form.Label>
                  <Form.Control as="textarea" rows={4} value={callResponse} onChange={(event) => setCallResponse(event.target.value)} style={EDITABLE_TEXT_CONTROL_STYLE} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Inbound SMS Response: "Text" (real person) <span className="text-danger">{'{{TEXT_RESPONSE}}'}</span></Form.Label>
                  <Form.Control as="textarea" rows={4} value={textResponse} onChange={(event) => setTextResponse(event.target.value)} style={EDITABLE_TEXT_CONTROL_STYLE} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Inbound SMS Response: "No" <span className="text-danger">{'{{NO_FINAL_RESPONSE}}'}</span></Form.Label>
                  <Form.Control as="textarea" rows={4} value={noFinalResponse} onChange={(event) => setNoFinalResponse(event.target.value)} style={EDITABLE_TEXT_CONTROL_STYLE} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Inbound SMS Response: Other / Invalid <span className="text-danger">{'{{INVALID_RESPONSE}}'}</span></Form.Label>
                  <Form.Control as="textarea" rows={4} value={invalidResponse} onChange={(event) => setInvalidResponse(event.target.value)} style={EDITABLE_TEXT_CONTROL_STYLE} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Optional Offer SMS (Appointment 2) <span className="text-danger">{'{{OFFER2_TEXT}}'}</span></Form.Label>
                  <Form.Control as="textarea" rows={6} value={offer2Text} onChange={(event) => setOffer2Text(event.target.value)} style={EDITABLE_TEXT_CONTROL_STYLE} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Optional Offer SMS (Appointment 3) <span className="text-danger">{'{{OFFER3_TEXT}}'}</span></Form.Label>
                  <Form.Control as="textarea" rows={6} value={offer3Text} onChange={(event) => setOffer3Text(event.target.value)} style={EDITABLE_TEXT_CONTROL_STYLE} />
                </Form.Group>
              </Col>
            </Row>
            ) : (
            <Row className="g-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Outbound SMS Preview</Form.Label>
                  <Form.Control as="textarea" rows={8} value={outboundText} readOnly />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Yes Response</Form.Label>
                  <Form.Control as="textarea" rows={4} value={yesResponse} readOnly />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>No Response</Form.Label>
                  <Form.Control as="textarea" rows={4} value={noFinalResponse} readOnly />
                </Form.Group>
              </Col>
            </Row>
            )}
          </Card.Body>
        </Card>

        <Row className="g-3 mt-1">
          <Col md={12}>
            <Card className="bg-light border">
              <Card.Body>
                <div className="small text-muted mb-2">Reply Flow Preview</div>
                <div className="small"><strong>Yes:</strong> {yesResponse}</div>
                <div className="small mt-2"><strong>No (1st no):</strong> {String(date2 || '').trim() ? 'Offer Optional Appointment 2' : noFinalResponse}</div>
                {String(date2 || '').trim() && (
                  <div className="small mt-1">{offer2Text}</div>
                )}
                <div className="small mt-2"><strong>No (2nd no):</strong> {String(date3 || '').trim() ? 'Offer Optional Appointment 3' : noFinalResponse}</div>
                {String(date3 || '').trim() && String(date2 || '').trim() && (
                  <div className="small mt-1">{offer3Text}</div>
                )}
                <div className="small mt-2"><strong>Call:</strong> {callResponse}</div>
                <div className="small mt-2"><strong>Text:</strong> {textResponse}</div>
                <div className="small mt-2"><strong>Anything else:</strong> {invalidResponse}</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {!messageValidation.ok && (
          <Row className="mt-3">
            <Col>
              <Alert variant="warning" className="mb-0">
                <strong>This message is not ready to send.</strong>
                <p className="mb-2 mt-1" style={{ fontSize: '0.9rem' }}>
                  The following would be sent to the customer as literal text:
                </p>
                <ul className="mb-0" style={{ fontSize: '0.9rem' }}>
                  {messageValidation.tokens.map((token) => (
                    <li key={token}>
                      <code>{token}</code>
                      {messageConfigService.PLACEHOLDER_HINTS[token]
                        ? ` — ${messageConfigService.PLACEHOLDER_HINTS[token]}`
                        : ' — not a recognised placeholder; check the wording in Message Text'}
                    </li>
                  ))}
                </ul>
              </Alert>
            </Col>
          </Row>
        )}

        <Row className="mt-3">
          <Col className="d-flex justify-content-end">
            {isSmsAdmin && (
              <Button variant="outline-secondary" className="me-2" href="/sms-templates">
                Manage In Template Builder
              </Button>
            )}
            <Button
              onClick={handleStartBooking}
              disabled={sending || loadingContact || !messageValidation.ok}
              title={messageValidation.ok ? undefined : 'Resolve the placeholders listed above before sending'}
            >
              {sending ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Starting...
                </>
              ) : 'Start Text Booking'}
            </Button>
          </Col>
        </Row>
      </Card.Body>
    </Card>

      {/* Manage Booking Options Modal */}
      {isSmsAdmin && (
      <Modal show={showOptionsModal} onHide={() => { setShowOptionsModal(false); setEditingItem(null); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>&#9881; Manage Booking Options</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex gap-2 mb-3 flex-wrap">
            {[['assessors', 'Assessors'], ['schemes', 'Schemes'], ['appointmentTypes', 'Appointment Types']].map(([key, label]) => (
              <Button key={key} size="sm" variant={optionsSection === key ? 'primary' : 'outline-secondary'}
                onClick={() => { setOptionsSection(key); setEditingItem(null); }}>
                {label}
              </Button>
            ))}
          </div>

          {optionsSection === 'assessors' && (
            <div>
              <p className="text-muted small mb-2">Add or remove assessor names shown in the booking dropdown.</p>
              {bookingOptions.assessorOptions.map((name, idx) => (
                <div key={idx} className="d-flex align-items-center gap-2 mb-2">
                  {editingItem?.section === 'assessors' && editingItem?.index === idx ? (
                    <>
                      <Form.Control size="sm" value={editingItem.draft} onChange={(e) => setEditingItem((prev) => ({ ...prev, draft: e.target.value }))} />
                      <Button size="sm" variant="success" onClick={() => {
                        const updated = [...bookingOptions.assessorOptions];
                        updated[idx] = editingItem.draft;
                        markOptionsDirty(); setBookingOptions((prev) => ({ ...prev, assessorOptions: updated }));
                        setEditingItem(null);
                      }}>Save</Button>
                      <Button size="sm" variant="outline-secondary" onClick={() => setEditingItem(null)}>Cancel</Button>
                    </>
                  ) : (
                    <>
                      <span className="flex-grow-1">{name}</span>
                      <Button size="sm" variant="outline-primary" onClick={() => setEditingItem({ section: 'assessors', index: idx, draft: name })}>Edit</Button>
                      <Button size="sm" variant="outline-danger" onClick={() => {
                        markOptionsDirty(); setBookingOptions((prev) => ({ ...prev, assessorOptions: prev.assessorOptions.filter((_, i) => i !== idx) }));
                        if (assessor === name) setAssessor(bookingOptions.assessorOptions.find((n) => n !== name) || '');
                      }}>Remove</Button>
                    </>
                  )}
                </div>
              ))}
              {editingItem?.section === 'assessors' && editingItem?.index === -1 ? (
                <div className="d-flex gap-2 mt-3">
                  <Form.Control size="sm" placeholder="Assessor name" value={editingItem.draft} onChange={(e) => setEditingItem((prev) => ({ ...prev, draft: e.target.value }))} />
                  <Button size="sm" variant="success" onClick={() => {
                    if (String(editingItem.draft || '').trim()) {
                      markOptionsDirty(); setBookingOptions((prev) => ({ ...prev, assessorOptions: [...prev.assessorOptions, editingItem.draft.trim()] }));
                    }
                    setEditingItem(null);
                  }}>Add</Button>
                  <Button size="sm" variant="outline-secondary" onClick={() => setEditingItem(null)}>Cancel</Button>
                </div>
              ) : (
                <Button size="sm" variant="outline-primary" className="mt-2" onClick={() => setEditingItem({ section: 'assessors', index: -1, draft: '' })}>+ Add Assessor</Button>
              )}
            </div>
          )}

          {optionsSection === 'schemes' && (
            <div>
              <p className="text-muted small mb-2">Each scheme supplies its display label, the name used in SMS text, company name, and contact phone number.</p>
              {bookingOptions.schemeOptions.map((scheme, idx) => (
                <Card key={scheme.id || idx} className="mb-2">
                  <Card.Body className="py-2">
                    {editingItem?.section === 'schemes' && editingItem?.index === idx ? (
                      <>
                        <Row className="g-2 mb-2">
                          <Col md={4}><Form.Group><Form.Label className="small mb-1">ID (key)</Form.Label><Form.Control size="sm" value={editingItem.draft.id || ''} onChange={(e) => setEditingItem((p) => ({ ...p, draft: { ...p.draft, id: e.target.value } }))} /></Form.Group></Col>
                          <Col md={4}><Form.Group><Form.Label className="small mb-1">Dropdown Label</Form.Label><Form.Control size="sm" value={editingItem.draft.label || ''} onChange={(e) => setEditingItem((p) => ({ ...p, draft: { ...p.draft, label: e.target.value } }))} /></Form.Group></Col>
                          <Col md={4}><Form.Group><Form.Label className="small mb-1">Company Name</Form.Label><Form.Control size="sm" value={editingItem.draft.company || ''} onChange={(e) => setEditingItem((p) => ({ ...p, draft: { ...p.draft, company: e.target.value } }))} /></Form.Group></Col>
                          <Col md={8}><Form.Group><Form.Label className="small mb-1">Scheme Name (used in SMS text)</Form.Label><Form.Control size="sm" value={editingItem.draft.schemeName || ''} onChange={(e) => setEditingItem((p) => ({ ...p, draft: { ...p.draft, schemeName: e.target.value } }))} /></Form.Group></Col>
                          <Col md={4}><Form.Group><Form.Label className="small mb-1">Phone Number</Form.Label><Form.Control size="sm" value={editingItem.draft.schemeNumber || ''} onChange={(e) => setEditingItem((p) => ({ ...p, draft: { ...p.draft, schemeNumber: e.target.value } }))} /></Form.Group></Col>
                        </Row>
                        <div className="d-flex gap-2">
                          <Button size="sm" variant="success" onClick={() => {
                            const updated = [...bookingOptions.schemeOptions];
                            updated[idx] = editingItem.draft;
                            markOptionsDirty(); setBookingOptions((prev) => ({ ...prev, schemeOptions: updated }));
                            setEditingItem(null);
                          }}>Save</Button>
                          <Button size="sm" variant="outline-secondary" onClick={() => setEditingItem(null)}>Cancel</Button>
                        </div>
                      </>
                    ) : (
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{scheme.label}</strong>
                          <div className="text-muted small">{scheme.company} | {scheme.schemeNumber}</div>
                          <div className="text-muted small">{scheme.schemeName}</div>
                        </div>
                        <div className="d-flex gap-2">
                          <Button size="sm" variant="outline-primary" onClick={() => setEditingItem({ section: 'schemes', index: idx, draft: { ...scheme } })}>Edit</Button>
                          <Button size="sm" variant="outline-danger" onClick={() => {
                            markOptionsDirty(); setBookingOptions((prev) => ({ ...prev, schemeOptions: prev.schemeOptions.filter((_, i) => i !== idx) }));
                            if (schemeId === scheme.id) setSchemeId(bookingOptions.schemeOptions.find((s) => s.id !== scheme.id)?.id || '');
                          }}>Remove</Button>
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              ))}
              {editingItem?.section === 'schemes' && editingItem?.index === -1 ? (
                <Card className="border-primary mt-2">
                  <Card.Body className="py-2">
                    <Row className="g-2 mb-2">
                      <Col md={4}><Form.Group><Form.Label className="small mb-1">ID (key)</Form.Label><Form.Control size="sm" placeholder="e.g. GLA" value={editingItem.draft.id || ''} onChange={(e) => setEditingItem((p) => ({ ...p, draft: { ...p.draft, id: e.target.value } }))} /></Form.Group></Col>
                      <Col md={4}><Form.Group><Form.Label className="small mb-1">Dropdown Label</Form.Label><Form.Control size="sm" placeholder="e.g. GLA" value={editingItem.draft.label || ''} onChange={(e) => setEditingItem((p) => ({ ...p, draft: { ...p.draft, label: e.target.value } }))} /></Form.Group></Col>
                      <Col md={4}><Form.Group><Form.Label className="small mb-1">Company Name</Form.Label><Form.Control size="sm" placeholder="e.g. Happy Energy Solutions" value={editingItem.draft.company || ''} onChange={(e) => setEditingItem((p) => ({ ...p, draft: { ...p.draft, company: e.target.value } }))} /></Form.Group></Col>
                      <Col md={8}><Form.Group><Form.Label className="small mb-1">Scheme Name (used in SMS)</Form.Label><Form.Control size="sm" placeholder="e.g. the Greater London Authority..." value={editingItem.draft.schemeName || ''} onChange={(e) => setEditingItem((p) => ({ ...p, draft: { ...p.draft, schemeName: e.target.value } }))} /></Form.Group></Col>
                      <Col md={4}><Form.Group><Form.Label className="small mb-1">Phone Number</Form.Label><Form.Control size="sm" placeholder="e.g. 0115 784 3060" value={editingItem.draft.schemeNumber || ''} onChange={(e) => setEditingItem((p) => ({ ...p, draft: { ...p.draft, schemeNumber: e.target.value } }))} /></Form.Group></Col>
                    </Row>
                    <div className="d-flex gap-2">
                      <Button size="sm" variant="success" onClick={() => {
                        if (editingItem.draft.id?.trim() && editingItem.draft.label?.trim()) {
                          markOptionsDirty(); setBookingOptions((prev) => ({ ...prev, schemeOptions: [...prev.schemeOptions, { ...editingItem.draft }] }));
                        }
                        setEditingItem(null);
                      }}>Add Scheme</Button>
                      <Button size="sm" variant="outline-secondary" onClick={() => setEditingItem(null)}>Cancel</Button>
                    </div>
                  </Card.Body>
                </Card>
              ) : (
                <Button size="sm" variant="outline-primary" className="mt-2" onClick={() => setEditingItem({ section: 'schemes', index: -1, draft: { id: '', label: '', schemeName: '', company: '', schemeNumber: '' } })}>+ Add Scheme</Button>
              )}
            </div>
          )}

          {optionsSection === 'appointmentTypes' && (
            <div>
              <p className="text-muted small mb-2">The ID must match the SMS template name suffix. Body and duration are used in the booking SMS text.</p>
              {bookingOptions.appointmentTypes.map((appt, idx) => (
                <Card key={appt.id || idx} className="mb-2">
                  <Card.Body className="py-2">
                    {editingItem?.section === 'appointmentTypes' && editingItem?.index === idx ? (
                      <>
                        <Row className="g-2 mb-2">
                          <Col md={4}><Form.Group><Form.Label className="small mb-1">ID (key)</Form.Label><Form.Control size="sm" value={editingItem.draft.id || ''} onChange={(e) => setEditingItem((p) => ({ ...p, draft: { ...p.draft, id: e.target.value } }))} /></Form.Group></Col>
                          <Col md={8}><Form.Group><Form.Label className="small mb-1">Dropdown Label</Form.Label><Form.Control size="sm" value={editingItem.draft.label || ''} onChange={(e) => setEditingItem((p) => ({ ...p, draft: { ...p.draft, label: e.target.value } }))} /></Form.Group></Col>
                          <Col md={12}><Form.Group><Form.Label className="small mb-1">SMS Body Text</Form.Label><Form.Control as="textarea" rows={2} size="sm" value={editingItem.draft.body || ''} onChange={(e) => setEditingItem((p) => ({ ...p, draft: { ...p.draft, body: e.target.value } }))} /></Form.Group></Col>
                          <Col md={12}><Form.Group><Form.Label className="small mb-1">Duration Text</Form.Label><Form.Control size="sm" value={editingItem.draft.duration || ''} onChange={(e) => setEditingItem((p) => ({ ...p, draft: { ...p.draft, duration: e.target.value } }))} /></Form.Group></Col>
                        </Row>
                        <div className="d-flex gap-2">
                          <Button size="sm" variant="success" onClick={() => {
                            const updated = [...bookingOptions.appointmentTypes];
                            updated[idx] = editingItem.draft;
                            markOptionsDirty(); setBookingOptions((prev) => ({ ...prev, appointmentTypes: updated }));
                            setEditingItem(null);
                          }}>Save</Button>
                          <Button size="sm" variant="outline-secondary" onClick={() => setEditingItem(null)}>Cancel</Button>
                        </div>
                      </>
                    ) : (
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <strong>{appt.label}</strong>
                          <div className="text-muted small mt-1">Duration: {appt.duration}</div>
                          <div className="text-muted small">Body: {String(appt.body || '').substring(0, 70)}...</div>
                        </div>
                        <div className="d-flex gap-2">
                          <Button size="sm" variant="outline-primary" onClick={() => setEditingItem({ section: 'appointmentTypes', index: idx, draft: { ...appt } })}>Edit</Button>
                          <Button size="sm" variant="outline-danger" onClick={() => {
                            markOptionsDirty(); setBookingOptions((prev) => ({ ...prev, appointmentTypes: prev.appointmentTypes.filter((_, i) => i !== idx) }));
                            if (appointmentType === appt.id) setAppointmentType(bookingOptions.appointmentTypes.find((t) => t.id !== appt.id)?.id || 'WHA');
                          }}>Remove</Button>
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              ))}
              {editingItem?.section === 'appointmentTypes' && editingItem?.index === -1 ? (
                <Card className="border-primary mt-2">
                  <Card.Body className="py-2">
                    <Row className="g-2 mb-2">
                      <Col md={4}><Form.Group><Form.Label className="small mb-1">ID (key)</Form.Label><Form.Control size="sm" placeholder="e.g. SURVEY" value={editingItem.draft.id || ''} onChange={(e) => setEditingItem((p) => ({ ...p, draft: { ...p.draft, id: e.target.value } }))} /></Form.Group></Col>
                      <Col md={8}><Form.Group><Form.Label className="small mb-1">Dropdown Label</Form.Label><Form.Control size="sm" placeholder="e.g. Home Energy Survey" value={editingItem.draft.label || ''} onChange={(e) => setEditingItem((p) => ({ ...p, draft: { ...p.draft, label: e.target.value } }))} /></Form.Group></Col>
                      <Col md={12}><Form.Group><Form.Label className="small mb-1">SMS Body Text</Form.Label><Form.Control as="textarea" rows={2} size="sm" placeholder="e.g. to arrange the booking of a survey for" value={editingItem.draft.body || ''} onChange={(e) => setEditingItem((p) => ({ ...p, draft: { ...p.draft, body: e.target.value } }))} /></Form.Group></Col>
                      <Col md={12}><Form.Group><Form.Label className="small mb-1">Duration Text</Form.Label><Form.Control size="sm" placeholder="e.g. The assessment will take around 1 hour" value={editingItem.draft.duration || ''} onChange={(e) => setEditingItem((p) => ({ ...p, draft: { ...p.draft, duration: e.target.value } }))} /></Form.Group></Col>
                    </Row>
                    <div className="d-flex gap-2">
                      <Button size="sm" variant="success" onClick={() => {
                        if (editingItem.draft.id?.trim() && editingItem.draft.label?.trim()) {
                          markOptionsDirty(); setBookingOptions((prev) => ({ ...prev, appointmentTypes: [...prev.appointmentTypes, { ...editingItem.draft }] }));
                        }
                        setEditingItem(null);
                      }}>Add Appointment Type</Button>
                      <Button size="sm" variant="outline-secondary" onClick={() => setEditingItem(null)}>Cancel</Button>
                    </div>
                  </Card.Body>
                </Card>
              ) : (
                <Button size="sm" variant="outline-primary" className="mt-2" onClick={() => setEditingItem({ section: 'appointmentTypes', index: -1, draft: { id: '', label: '', body: '', duration: '' } })}>+ Add Appointment Type</Button>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="text-muted" style={{ fontSize: '0.8rem', flex: '1 1 auto' }}>
            {optionsStatus.loading && 'Loading shared options…'}
            {!optionsStatus.loading && optionsStatus.source === 'shared' && (
              <>Shared with all users
                {optionsStatus.updatedAt
                  ? ` — last saved ${new Date(optionsStatus.updatedAt).toLocaleString('en-GB')}`
                  : ''}
                {optionsStatus.updatedBy ? ` by ${optionsStatus.updatedBy}` : ''}
              </>
            )}
            {!optionsStatus.loading && optionsStatus.source !== 'shared' && (
              <span className="text-warning-emphasis">
                These options are only on this computer. Save to share them with everyone.
              </span>
            )}
            {optionsStatus.dirty && <strong className="ms-2">Unsaved changes</strong>}
            {optionsStatus.error && (
              <div className="text-danger mt-1">{optionsStatus.error}</div>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-danger" size="sm" onClick={() => {
              setBookingOptions({ appointmentTypes: Object.values(APPOINTMENT_TYPES), schemeOptions: SCHEME_OPTIONS, assessorOptions: ASSESSOR_OPTIONS });
              setEditingItem(null);
              markOptionsDirty();
            }}>Reset to Defaults</Button>
            <Button
              variant="primary"
              size="sm"
              onClick={publishBookingOptions}
              disabled={optionsStatus.saving || optionsStatus.loading}
            >
              {optionsStatus.saving
                ? (<><Spinner as="span" animation="border" size="sm" className="me-1" />Saving…</>)
                : 'Save for everyone'}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => { setShowOptionsModal(false); setEditingItem(null); }}>Close</Button>
          </div>
        </Modal.Footer>
      </Modal>
      )}
    </>
  );
};

export default KarmaTextBooking;
